import { MeasurementSystemEnum, MeasurementTypeEnum } from "service";
import {
  AdminCustomerResDto,
  AdminCustomerLocationResDto,
  AdminLocationWithCustomerResDto,
  UnitResDto,
  AdminNotificationResDto,
  AdminPutNotificationReqDto,
  AdminDailyWeatherForecastResDto,
} from "service/api/estabrookSchemas";
import { Duration, DateTime } from "luxon";
import { find, sortBy } from "lodash";
import { Color, Palette } from "@mui/material";

type MeasurementCombinedKey = `${MeasurementTypeEnum}_${MeasurementSystemEnum}`;
const measurementCombinedKeyLabelsMap: Record<
  MeasurementCombinedKey,
  { space: string }
> = {
  distance_imperial: {
    space: "",
  },
  distance_metric: {
    space: " ",
  },
  temperature_imperial: {
    space: "",
  },
  temperature_metric: {
    space: "",
  },
  speed_imperial: {
    space: " ",
  },
  speed_metric: {
    space: " ",
  },
};

export const weekdays = [
  { value: 1, label: "Sunday", short: "S" },
  { value: 2, label: "Monday", short: "M" },
  { value: 3, label: "Tuesday", short: "T" },
  { value: 4, label: "Wednesday", short: "W" },
  { value: 5, label: "Thursday", short: "T" },
  { value: 6, label: "Friday", short: "F" },
  { value: 7, label: "Saturday", short: "S" },
];

export const addressIso = (
  dto:
    | AdminCustomerLocationResDto
    | AdminLocationWithCustomerResDto
    | undefined,
): string => {
  if (!dto) {
    return "";
  }
  if (dto.address) {
    return dto.address;
  }
  if (!dto.street) {
    return "No address";
  }
  return `${dto.street}, ${dto.city}, ${dto.state}, ${dto.zipCode}`;
};

export const fullName = (
  dto: AdminCustomerResDto | AdminLocationWithCustomerResDto | undefined,
): string => {
  if (!dto) {
    return "";
  }
  return `${dto.firstName} ${dto.lastName}`;
};

export const formattedValueWithSymbol = (
  unit: UnitResDto,
  system: MeasurementSystemEnum = MeasurementSystemEnum.imperial,
): string => {
  return `${unit.value}${
    measurementCombinedKeyLabelsMap[`${unit.type}_${system}`].space
  }${unit.symbol}`;
};

export const formattedTemperature = (
  weather: AdminDailyWeatherForecastResDto,
): string => {
  return weather
    ? `${formattedValueWithSymbol(
        weather.day.temperature.high.imperial,
      )} / ${formattedValueWithSymbol(weather.day.temperature.low.imperial)}`
    : "N/A";
};

export const formattedSnow = (
  weather: AdminDailyWeatherForecastResDto,
): string => {
  return weather
    ? `${formattedValueWithSymbol(
        weather.day.snow.imperial,
      )} / ${formattedValueWithSymbol(weather.night.snow.imperial)}`
    : "N/A";
};

export const formatTime = (time?: number): string => {
  if (!time) {
    return " ";
  }
  return Duration.fromMillis(time * 1000).toFormat("hh:mm");
};

export const formatMinutesToHoursAndMinutes = (
  minutes: number | null | undefined,
): string => {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return "";

  const roundedMinutes = Math.round(minutes);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  if (hours === 0) return `${remainingMinutes}m`;
  return remainingMinutes === 0
    ? `${hours}h`
    : `${hours}h ${remainingMinutes}m`;
};

export const formatNumberToMinutes = (minutes: number | undefined): string => {
  if (!minutes) return "";
  const roundedMinutes = Math.round(minutes);

  // If minutes > 59, show hours and minutes
  if (roundedMinutes > 59) {
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  }

  return `${roundedMinutes.toLocaleString()} min`;
};

/**
 * Format minutes to a precise "5m 24s" format for live phase
 * Shows actual values without rounding to whole minutes
 */
export const formatNumberToMinutesPrecise = (
  minutes: number | undefined,
): string => {
  if (minutes === undefined || minutes === null) return "";
  if (minutes === 0) return "0s";

  const totalSeconds = Math.round(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  // Handle hours if needed
  if (mins > 59) {
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    if (secs === 0) {
      return remainingMins === 0 ? `${hours}h` : `${hours}h ${remainingMins}m`;
    }
    return remainingMins === 0
      ? `${hours}h ${secs}s`
      : `${hours}h ${remainingMins}m ${secs}s`;
  }

  // Less than an hour
  if (secs === 0) return `${mins}m`;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

export const formatDateTime = (
  date?: string,
  format: Intl.DateTimeFormatOptions = DateTime.DATETIME_SHORT,
): string => {
  if (!date) {
    return " ";
  }
  return DateTime.fromISO(String(date), { zone: "utc" })
    .toLocal()
    .toLocaleString(format);
};

export const formattedRecurrence = (
  values: AdminNotificationResDto | AdminPutNotificationReqDto,
  prepend = "Repeats every ",
): string => {
  return `${prepend}${
    values.customInterval! > 1 ? values.customInterval : ""
  } ${values.customPeriod}${values.customInterval! > 1 ? "s" : ""}${
    values.customWeekday!.length > 0 ? " on " : ""
  }${sortBy(values.customWeekday!).map(
    (value, index, array) =>
      `${find(weekdays, { value })?.label}${
        index === array.length - 1
          ? ""
          : index === array.length - 2
            ? " and "
            : ", "
      }`,
  )}${
    values.endDate
      ? ` until ${formatDateTime(values.endDate as string, DateTime.DATE_MED)}`
      : ""
  }`;
};

export const paletteColorStringRenderer = (
  palette: Palette,
  color: string | undefined,
  selected?: boolean,
  orderStatus?: string,
) => {
  const paletteColor = palette[color as keyof Palette] as Color | undefined;

  const hexCodeRegex = /#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/;
  let paletteColorType = undefined;

  if (orderStatus === "skipped" || orderStatus === "inProgress") {
    return selected ? "#454749" : "#75777A";
  }

  if (
    ["completed", "cantPlow", "partiallyCompleted"].includes(orderStatus || "")
  ) {
    return "#AAABAE";
  }

  // Enhanced selection logic: try different shades for darkening
  if (selected && paletteColor) {
    // Try to get a darker shade: 600 > 700 > 800 > 500
    return (
      paletteColor[600] ||
      paletteColor[700] ||
      paletteColor[800] ||
      paletteColor[500] ||
      paletteColor[400]
    );
  }

  if (orderStatus === "delayed" && paletteColor) {
    return paletteColor[300];
  }

  if (paletteColor && paletteColor[400]) {
    return paletteColor[400];
  }

  // For hex colors or unknown colors, manually darken them when selected
  if (color && hexCodeRegex.test(color)) {
    paletteColorType = color;
    if (selected) {
      // Convert hex to darker shade manually
      return darkenHexColor(color, 0.2); // Darken by 20%
    }
  }

  return paletteColorType;
};

// Helper function to darken hex colors
const darkenHexColor = (hex: string, factor: number): string => {
  // Remove the hash if present
  const colorHex = hex.replace("#", "");

  // Convert to RGB
  const num = parseInt(colorHex, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;

  // Darken each component
  const newR = Math.max(0, Math.floor(r * (1 - factor)));
  const newG = Math.max(0, Math.floor(g * (1 - factor)));
  const newB = Math.max(0, Math.floor(b * (1 - factor)));

  // Convert back to hex
  return `#${((newR << 16) | (newG << 8) | newB).toString(16).padStart(6, "0")}`;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};
